import * as THREE from 'three';

/**
 * ContactShadow - Creates soft contact shadows using render-to-texture technique
 * Based on: https://threejs.org/examples/webgl_shadow_contact.html
 */
// The room floor's shadow, which the AR export bakes too.
export const FLOOR_SHADOW_LOOK = { contactHeight: 0.5, blur: 0.06, darkness: 1.0, opacity: 0.85 };

export class ContactShadow {
  constructor(renderer, scene, options = {}) {
    this.renderer = renderer;
    this.scene = scene;

    // Configuration
    this.planeWidth = options.planeWidth || 10;
    this.planeHeight = options.planeHeight || 10;
    // Floor-to-underside gap the depth ramp spans (~0.3-0.5 m); room height instead flattens every silhouette to a uniform blob.
    this.contactHeight = options.contactHeight || 0.45;
    // Penumbra width in metres (per gaussian tap; the shader takes 4 each side).
    this.blur = options.blur || 0.12;
    this.darkness = options.darkness || 1.5;
    this.opacity = options.opacity || 1.0;
    this.renderTargetSize = options.renderTargetSize || 512;
    // The AR export's clones carry no furnitureElement, so it passes its own.
    this.casts = options.casts || ((object) => !!object.userData.furnitureElement);

    // Render targets for shadow and blur
    this.renderTarget = new THREE.WebGLRenderTarget(this.renderTargetSize, this.renderTargetSize);
    this.renderTarget.texture.generateMipmaps = false;

    this.renderTargetBlur = new THREE.WebGLRenderTarget(this.renderTargetSize, this.renderTargetSize);
    this.renderTargetBlur.texture.generateMipmaps = false;

    // At the floor looking UP, so depth 0 is the floor and the closest fragments are darkest;
    // NEAR_SLACK keeps geometry dipping below y=0 from clipping into a hole.
    const NEAR_SLACK = 0.02;
    this.shadowCamera = new THREE.OrthographicCamera(
      -this.planeWidth / 2,
      this.planeWidth / 2,
      this.planeHeight / 2,
      -this.planeHeight / 2,
      -NEAR_SLACK,
      this.contactHeight
    );
    this.shadowCamera.position.set(0, 0, 0);
    this.shadowCamera.rotation.set(Math.PI / 2, 0, 0);

    // Custom depth material for rendering shadows
    this.depthMaterial = new THREE.MeshDepthMaterial();
    this.depthMaterial.userData.darkness = { value: this.darkness };
    this.depthMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.darkness = this.depthMaterial.userData.darkness;
      shader.fragmentShader = /* glsl */`
        uniform float darkness;
        ${shader.fragmentShader.replace(
          'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
          'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * darkness );'
        )}
      `;
    };
    // Depth test ON, unlike the three.js example: with many meshes the last-drawn would otherwise win the pixel, not the lowest.
    this.depthMaterial.depthTest = true;
    this.depthMaterial.depthWrite = true;

    // Create shadow plane mesh (horizontal, on the floor)
    const shadowGeometry = new THREE.PlaneGeometry(this.planeWidth, this.planeHeight);
    shadowGeometry.rotateX(-Math.PI / 2); // Face upward
    // Imaging the floor from below flips handedness, so flip v; without this the shadow is mirrored across Z.
    const uv = shadowGeometry.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setY(i, 1 - uv.getY(i));
    uv.needsUpdate = true;

    const shadowMaterial = new THREE.MeshBasicMaterial({
      map: this.renderTarget.texture,
      opacity: this.opacity,
      transparent: true,
      depthWrite: false,
      blending: THREE.MultiplyBlending, // Multiply blending for realistic shadow compositing
      premultipliedAlpha: true, // Required for MultiplyBlending
    });

    this.enabled = true;
    this.shadowMesh = new THREE.Mesh(shadowGeometry, shadowMaterial);
    this.shadowMesh.position.set(0, 0.01, 0); // Slightly above floor to prevent z-fighting
    this.shadowMesh.renderOrder = 1;
    this.shadowMesh.name = 'contactShadow';

    // Create blur planes (for rendering blur passes)
    const blurPlaneGeometry = new THREE.PlaneGeometry(this.planeWidth, this.planeHeight);

    const blurMaterial = () => new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        texelStep: { value: new THREE.Vector2() },
      },
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getBlurShader(),
      depthTest: false
    });
    this.horizontalBlurMaterial = blurMaterial();
    this.verticalBlurMaterial = blurMaterial();

    this.blurPlane = new THREE.Mesh(blurPlaneGeometry);
    this.blurPlane.visible = false;

    // Separate scene for blur rendering
    this.blurScene = new THREE.Scene();
    this.blurScene.add(this.blurPlane);

    // Orthographic camera for blur passes
    this.blurCamera = new THREE.OrthographicCamera(
      -this.planeWidth / 2, this.planeWidth / 2,
      this.planeHeight / 2, -this.planeHeight / 2,
      0, 1
    );
  }

  getVertexShader() {
    return /* glsl */`
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
  }

  // 9-tap Gaussian along `texelStep` (one texel offset in UV): (h, 0) for the horizontal pass, (0, v) for the vertical.
  getBlurShader() {
    return /* glsl */`
      uniform sampler2D tDiffuse;
      uniform vec2 texelStep;
      varying vec2 vUv;

      void main() {
        vec4 sum = vec4(0.0);
        sum += texture2D(tDiffuse, vUv - 4.0 * texelStep) * 0.051;
        sum += texture2D(tDiffuse, vUv - 3.0 * texelStep) * 0.0918;
        sum += texture2D(tDiffuse, vUv - 2.0 * texelStep) * 0.12245;
        sum += texture2D(tDiffuse, vUv - 1.0 * texelStep) * 0.1531;
        sum += texture2D(tDiffuse, vUv) * 0.1633;
        sum += texture2D(tDiffuse, vUv + 1.0 * texelStep) * 0.1531;
        sum += texture2D(tDiffuse, vUv + 2.0 * texelStep) * 0.12245;
        sum += texture2D(tDiffuse, vUv + 3.0 * texelStep) * 0.0918;
        sum += texture2D(tDiffuse, vUv + 4.0 * texelStep) * 0.051;
        gl_FragColor = sum;
      }
    `;
  }

  blurShadow(amount) {
    this.blurPlane.visible = true;

    // Tap spacing IS the blur amount; metres over plane width, so the penumbra keeps its floor width when the room resizes.
    const step = amount / this.planeWidth;
    this.horizontalBlurMaterial.uniforms.texelStep.value.set(step, 0);
    this.verticalBlurMaterial.uniforms.texelStep.value.set(0, step);

    // Horizontal blur
    this.blurPlane.material = this.horizontalBlurMaterial;
    this.blurPlane.material.uniforms.tDiffuse.value = this.renderTarget.texture;
    this.renderer.setRenderTarget(this.renderTargetBlur);
    this.renderer.render(this.blurPlane, this.blurCamera);

    // Vertical blur
    this.blurPlane.material = this.verticalBlurMaterial;
    this.blurPlane.material.uniforms.tDiffuse.value = this.renderTargetBlur.texture;
    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.render(this.blurPlane, this.blurCamera);

    this.blurPlane.visible = false;
  }

  render() {
    // Hide the shadow mesh itself during depth rendering
    const shadowMeshWasVisible = this.shadowMesh.visible;
    this.shadowMesh.visible = false;

    // Hide all non-furniture objects so only furniture casts shadows
    const hiddenObjects = [];
    this.scene.traverse((object) => {
      if (!object.visible) return;
      // Only process renderable objects (meshes, lines, sprites)
      if (!object.isMesh && !object.isLine && !object.isLineSegments && !object.isSprite && !object.isPoints) return;
      if (this.casts(object)) return;
      // Hide everything else (grid, dimensions, doors, room structure, etc.)
      hiddenObjects.push(object);
      object.visible = false;
    });

    // Store original values
    const initialBackground = this.scene.background;
    const initialOverrideMaterial = this.scene.overrideMaterial;
    const initialClearAlpha = this.renderer.getClearAlpha();

    // Update shadow camera
    this.shadowCamera.updateMatrixWorld();
    this.shadowCamera.updateProjectionMatrix();

    // Render depth to renderTarget
    this.scene.background = null;
    this.scene.overrideMaterial = this.depthMaterial;
    this.renderer.setClearAlpha(0); // Clear to transparent

    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.clear();
    this.renderer.render(this.scene, this.shadowCamera);

    // Restore original values
    this.scene.background = initialBackground;
    this.scene.overrideMaterial = initialOverrideMaterial;
    this.renderer.setClearAlpha(initialClearAlpha);

    // Apply blur passes
    this.blurShadow(this.blur);
    this.blurShadow(this.blur * 0.4);

    // Reset render target
    this.renderer.setRenderTarget(null);

    // Restore visibility of hidden objects
    hiddenObjects.forEach(obj => obj.visible = true);

    // Restore shadow mesh visibility
    this.shadowMesh.visible = shadowMeshWasVisible;
  }

  getMesh() {
    return this.shadowMesh;
  }

  /** Fade out without losing the configured opacity, so a re-enable restores the authored strength. */
  setEnabled(enabled) {
    this.enabled = enabled;
    this.shadowMesh.material.opacity = enabled ? this.opacity : 0;
  }

  dispose() {
    this.renderTarget.dispose();
    this.renderTargetBlur.dispose();
    this.horizontalBlurMaterial.dispose();
    this.verticalBlurMaterial.dispose();
    this.depthMaterial.dispose();
    this.blurPlane.geometry.dispose();
    this.shadowMesh.geometry.dispose();
    this.shadowMesh.material.dispose();
  }
}
